export const UNAVAILABLE_STATISTICS_CALCULATORS = [
  "Prior Posterior",
  "Bayes Factor",
  "MCMC",
  "Bootstrap",
  "Monte Carlo",
  "Random Number",
  "Information Gain",
  "Mutual Information",
  "KL Divergence",
  "Cross Entropy",
  "Survival Analysis",
  "Kaplan Meier",
  "ANCOVA",
  "Repeated Measures",
  "Simulation",
  "Weighting",
  "Calibration Weight",
] as const;

type UnavailableStatisticsCalculator =
  (typeof UNAVAILABLE_STATISTICS_CALCULATORS)[number];

const unavailableTitles = new Set<string>(UNAVAILABLE_STATISTICS_CALCULATORS);

const unavailableRelatedCalculators: Record<
  UnavailableStatisticsCalculator,
  { slug: string; context: string }
> = {
  "Prior Posterior": {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  "Bayes Factor": {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  MCMC: {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  Bootstrap: {
    slug: "statistics-mean-calculator",
    context: "a basic descriptive summary",
  },
  "Monte Carlo": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Random Number": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Information Gain": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Mutual Information": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "KL Divergence": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Cross Entropy": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Survival Analysis": {
    slug: "statistics-confidence-interval-calculator",
    context: "a supported confidence-interval estimate",
  },
  "Kaplan Meier": {
    slug: "statistics-confidence-interval-calculator",
    context: "a supported confidence-interval estimate",
  },
  ANCOVA: {
    slug: "statistics-mean-calculator",
    context: "a basic descriptive summary",
  },
  "Repeated Measures": {
    slug: "statistics-mean-calculator",
    context: "a basic descriptive summary",
  },
  Simulation: {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability calculation",
  },
  Weighting: {
    slug: "statistics-mean-calculator",
    context: "a basic unweighted descriptive summary",
  },
  "Calibration Weight": {
    slug: "statistics-mean-calculator",
    context: "a basic unweighted descriptive summary",
  },
};

const activeStatisticsDescriptions: Record<string, string> = {
  Mean:
    "Calculate the accepted-value count, sum, and arithmetic mean from finite numeric data; ignored tokens are listed.",
  Median:
    "Sort the accepted finite numeric values and calculate the median, averaging the two middle values when the count is even.",
  Mode:
    "Report the most frequent accepted numeric value or tied values with their frequencies; identify lists with no repeated value.",
  "Standard Deviation":
    "Calculate population standard deviation σ and sample standard deviation s from the same accepted finite values, with both formulas shown.",
  Variance:
    "Calculate population variance σ² and sample variance s² from the accepted finite values, using n and n − 1 denominators.",
  "Interquartile Range":
    "Calculate Q1, Q3, and IQR with the inclusive QUARTILE.INC method and linear interpolation.",
  Skewness:
    "Calculate population Fisher–Pearson skewness g1 and adjusted sample skewness G1; requires at least three values and non-zero spread.",
  Kurtosis:
    "Calculate population excess kurtosis g2 and adjusted Excel KURT estimate G2; requires at least four values and non-zero spread.",
  "Survey Design":
    "Estimate the sample size for a survey from confidence level, expected proportion, margin of error, and an optional finite-population correction.",
  "Response Rate":
    "Calculate survey response rate as completed interviews divided by eligible sampled units, where eligible units equal sampled units minus ineligible units.",
  "Cluster Sample":
    "Estimate the cluster-sampling design effect from average cluster size and intraclass correlation, then compare effective and inflated sample sizes.",
  "Post Stratification":
    "Calculate post-stratification weights and weighted stratum totals from population and sample shares; flag strata with no sampled cases.",
  "Stratified Sample":
    "Allocate a total sample proportionally or by Neyman allocation across strata, using largest-remainder rounding to preserve the requested total.",
  "Quota Sample":
    "Allocate a sample across quota shares with largest-remainder rounding. Quota sampling is non-probability sampling, and no margin of error is computed.",
  "Systematic Sample":
    "Select systematic sample units from a population using a random start and interval, with an explicit floor rule for fractional intervals.",
  "Hazard Ratio":
    "Compare exposed and reference event rates per person-time using an incidence-rate ratio and log-scale 95% confidence interval; this is not a Cox model.",
  "Poisson Distribution":
    "Calculate P(X = k), P(X ≤ k), and P(X ≥ k) with a Poisson count model, alongside its mean, variance, and standard deviation. Assumes independent events at a constant average rate.",
  "Bayesian Inference":
    "Apply Bayes' theorem to a binary hypothesis using a prior probability, sensitivity, and false-positive rate; view the evidence probability, posterior probability, and change from the prior.",
};

export const IMPLEMENTED_STATISTICS_SPECIALIST_TITLES = [
  "Survey Design",
  "Response Rate",
  "Cluster Sample",
  "Post Stratification",
  "Stratified Sample",
  "Quota Sample",
  "Systematic Sample",
  "Hazard Ratio",
] as const;

const implementedStatisticsTitles = new Set<string>(
  IMPLEMENTED_STATISTICS_SPECIALIST_TITLES,
);

export function getStatisticsCalculatorTitle(title: string): string {
  return title.replace(/\s+calculator$/i, "").trim();
}

export function isImplementedStatisticsSpecialist(title: string): boolean {
  return implementedStatisticsTitles.has(getStatisticsCalculatorTitle(title));
}

export function getStatisticsUnavailableCalculatorProfile(title: string) {
  const topic = getStatisticsCalculatorTitle(title);
  if (!unavailableTitles.has(topic)) return null;
  const related = unavailableRelatedCalculators[topic as UnavailableStatisticsCalculator];
  const notice = `This calculator is not available yet. We're building a dedicated version of the ${topic} calculator.`;
  return {
    topic,
    notice,
    seoDescription: `${notice} No input fields or calculated results are shown on this page yet.`,
    includeInteractiveSchemas: false as const,
    related,
  };
}

export function getStatisticsCalculatorDescription(title: string): string | undefined {
  const topic = getStatisticsCalculatorTitle(title);
  const unavailable = getStatisticsUnavailableCalculatorProfile(topic);
  if (unavailable) return unavailable.seoDescription;
  return activeStatisticsDescriptions[topic];
}

export function getStatisticsUnavailableRelatedCalculator(title: string) {
  return getStatisticsUnavailableCalculatorProfile(title)?.related ?? null;
}

export function getStatisticsUnavailableFaqItems(title: string) {
  const profile = getStatisticsUnavailableCalculatorProfile(title);
  if (!profile) return null;
  return [
    {
      question: `Is the ${profile.topic} calculator available?`,
      answer: profile.notice,
    },
    {
      question: `Does this page calculate ${profile.topic}?`,
      answer:
        "No. It currently has no input fields and displays no calculated results.",
    },
  ];
}

const descriptiveStatisticsFaqs: Record<
  string,
  Array<{ question: string; answer: string }>
> = {
  Mean: [
    {
      question: "How does the Mean Calculator find the arithmetic mean?",
      answer:
        "It adds the accepted finite numeric values and divides by their count. This is the unweighted arithmetic mean.",
    },
    {
      question: "Which entries are included in the mean?",
      answer:
        "The input accepts comma- or whitespace-separated numbers. Non-numeric and non-finite tokens are ignored and listed separately.",
    },
  ],
  Median: [
    {
      question: "How does the Median Calculator find the median?",
      answer:
        "It sorts the accepted finite numeric values. The median is the middle value for an odd count, or the average of the two middle values for an even count.",
    },
    {
      question: "Does the median change when I reorder the values?",
      answer:
        "No. Sorting the accepted values before finding the middle position makes the result independent of their input order.",
    },
  ],
  "Standard Deviation": [
    {
      question: "What is the difference between population σ and sample s?",
      answer:
        "Population σ divides the sum of squared deviations by n before taking the square root. Sample s uses n − 1; the page reports both from the same accepted values.",
    },
    {
      question: "What units does standard deviation use?",
      answer:
        "Standard deviation is expressed in the same units as the observations because the square root returns squared deviations to the original scale.",
    },
  ],
  Variance: [
    {
      question: "What is the difference between population and sample variance?",
      answer:
        "Population variance divides the sum of squared deviations by n. Sample variance divides by n − 1; the page shows both calculations from the same values.",
    },
    {
      question: "Why are variance units squared?",
      answer:
        "Variance averages squared deviations from the mean, so its units are the square of the input unit. Standard deviation is the corresponding measure in the original units.",
    },
  ],
  Mode: [
    {
      question: "How does the Mode Calculator find the mode?",
      answer:
        "It counts the exact frequency of each accepted numeric value and reports the value or values with the highest frequency. Tied values are all reported as modes.",
    },
    {
      question: "What does it mean when the calculator reports no mode?",
      answer:
        "It means every accepted value occurs once, so none occurs more often than the others.",
    },
  ],
  "Interquartile Range": [
    {
      question: "How does the Interquartile Range Calculator find Q1 and Q3?",
      answer:
        "It sorts the accepted values and uses inclusive linear interpolation at positions (n − 1) × 0.25 and (n − 1) × 0.75 to find Q1 and Q3.",
    },
    {
      question: "What does the interquartile range describe?",
      answer:
        "The IQR is Q3 − Q1, the spread between the lower and upper quartiles. It describes the middle half of the ordered values, not the full minimum-to-maximum range.",
    },
  ],
  Skewness: [
    {
      question: "Which skewness values does the calculator report?",
      answer:
        "It reports population moment skewness g1 and the adjusted Fisher–Pearson sample coefficient G1. G1 applies a finite-sample correction to g1.",
    },
    {
      question: "Does skewness determine whether a dataset is normal?",
      answer:
        "No. Skewness summarizes asymmetry in the entered values; it is not a normality test or a conclusion about a larger population.",
    },
  ],
  Kurtosis: [
    {
      question: "Which kurtosis values does the calculator report?",
      answer:
        "It reports population excess kurtosis g2 and an adjusted sample excess kurtosis G2. Excess kurtosis is the standardized fourth moment minus 3.",
    },
    {
      question: "Does kurtosis determine whether a dataset is normal?",
      answer:
        "No. Kurtosis summarizes a feature of the entered distribution's shape; the displayed coefficients are descriptive statistics, not a normality test.",
    },
  ],
};

export function getDescriptiveStatisticsFaqItems(title: string) {
  return descriptiveStatisticsFaqs[getStatisticsCalculatorTitle(title)] ?? null;
}